import { defineMessages } from '@/i18n'

/** Bonus page and its settings dialog. Workbook terms in quotes ("Topli obrok" …) stay as AU has them. */
export const bonusMessages = defineMessages({
  en: {
    title: 'Bonus',
    subtitle: 'Worker payout per crew — production earnings, output per hour, norm and bonuses.',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    firma: 'Firma',
    allFirms: 'All firms',
    settings: 'Settings',
    configNote: 'Norm {norm}/h · meal {meal}/worker-day · leader bonus {leader}/day. Rates & thresholds seeded from the workbook — tune them in Settings to match AU.',
    colCrew: 'Crew (leader)',
    colFirma: 'Firma',
    colDays: 'Days',
    colMetres: 'Metres',
    colHours: 'Hours',
    colEarnings: 'Earnings',
    colPerHour: '€/h',
    colNorm: 'Norm',
    colMeal: 'Meal',
    colLeader: 'Leader',
    colTotal: 'Total',
    noProduction: 'No production this month.',
    totalCrews: { one: 'Total · {count} crew', other: 'Total · {count} crews' },
    // Settings dialog
    settingsTitle: 'Bonus settings',
    settingsSubtitle: 'Thresholds used by the calculator.',
    normTarget: 'Norm target (€ per hour)',
    normTargetHint: '"Ispunjena norma" = € per hour ÷ this',
    mealAllowance: 'Meal allowance (€ per worker-day)',
    crewLeaderBonus: 'Crew-leader bonus (€ per crew-day)',
    newWorkerBonus: 'New-worker bonus (€)',
    newWorkerWindow: '"New worker" window (days)',
    newWorkerWindowHint: 'hired within N days',
    newWorkerNote: "New-worker bonuses need a per-worker roster with hire dates, which isn't in the read model yet — these amounts are stored but not applied. Norm, meal and crew-leader bonus are live.",
    couldNotSave: 'Could not save.',
    saving: 'Saving…',
  },
  de: {
    title: 'Bonus',
    subtitle: 'Auszahlung an Arbeiter je Kolonne — Produktionserlös, Leistung pro Stunde, Norm und Boni.',
    previousMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    firma: 'Firma',
    allFirms: 'Alle Firmen',
    settings: 'Einstellungen',
    configNote: 'Norm {norm}/h · Verpflegung {meal}/Arbeitertag · Kolonnenführer-Bonus {leader}/Tag. Sätze und Schwellen stammen aus der Arbeitsmappe — passen Sie sie in den Einstellungen an AU an.',
    colCrew: 'Kolonne (Führer)',
    colFirma: 'Firma',
    colDays: 'Tage',
    colMetres: 'Meter',
    colHours: 'Stunden',
    colEarnings: 'Erlös',
    colPerHour: '€/h',
    colNorm: 'Norm',
    colMeal: 'Verpflegung',
    colLeader: 'Führer',
    colTotal: 'Gesamt',
    noProduction: 'Keine Produktion in diesem Monat.',
    totalCrews: { one: 'Gesamt · {count} Kolonne', other: 'Gesamt · {count} Kolonnen' },
    settingsTitle: 'Bonus-Einstellungen',
    settingsSubtitle: 'Schwellenwerte für die Berechnung.',
    normTarget: 'Normziel (€ pro Stunde)',
    normTargetHint: '„Ispunjena norma“ = € pro Stunde ÷ dieser Wert',
    mealAllowance: 'Verpflegungszuschuss (€ pro Arbeitertag)',
    crewLeaderBonus: 'Kolonnenführer-Bonus (€ pro Kolonnentag)',
    newWorkerBonus: 'Bonus für neue Arbeiter (€)',
    newWorkerWindow: 'Zeitraum „neuer Arbeiter“ (Tage)',
    newWorkerWindowHint: 'eingestellt innerhalb von N Tagen',
    newWorkerNote: 'Boni für neue Arbeiter brauchen eine Arbeiterliste mit Einstellungsdaten, die das Lesemodell noch nicht hat — diese Beträge werden gespeichert, aber nicht angewendet. Norm, Verpflegung und Kolonnenführer-Bonus sind aktiv.',
    couldNotSave: 'Speichern fehlgeschlagen.',
    saving: 'Wird gespeichert…',
  },
  cnr: {
    title: 'Bonus',
    subtitle: 'Isplata radnicima po koloni — zarada od proizvodnje, učinak po satu, norma i bonusi.',
    previousMonth: 'Prethodni mjesec',
    nextMonth: 'Sljedeći mjesec',
    firma: 'Firma',
    allFirms: 'Sve firme',
    settings: 'Podešavanja',
    configNote: 'Norma {norm}/h · obrok {meal}/radni dan radnika · bonus vođe {leader}/dan. Stope i pragovi preuzeti su iz radne sveske — prilagodite ih u Podešavanjima da odgovaraju AU.',
    colCrew: 'Kolona (vođa)',
    colFirma: 'Firma',
    colDays: 'Dani',
    colMetres: 'Metri',
    colHours: 'Sati',
    colEarnings: 'Zarada',
    colPerHour: '€/h',
    colNorm: 'Norma',
    colMeal: 'Obrok',
    colLeader: 'Vođa',
    colTotal: 'Ukupno',
    noProduction: 'Nema proizvodnje ovog mjeseca.',
    totalCrews: { one: 'Ukupno · {count} kolona', few: 'Ukupno · {count} kolone', other: 'Ukupno · {count} kolona' },
    settingsTitle: 'Podešavanja bonusa',
    settingsSubtitle: 'Pragovi koje koristi obračun.',
    normTarget: 'Ciljna norma (€ po satu)',
    normTargetHint: '"Ispunjena norma" = € po satu ÷ ova vrijednost',
    mealAllowance: 'Topli obrok (€ po radnom danu radnika)',
    crewLeaderBonus: 'Bonus vođe kolone (€ po danu kolone)',
    newWorkerBonus: 'Bonus za nove radnike (€)',
    newWorkerWindow: 'Period „novog radnika” (dani)',
    newWorkerWindowHint: 'zaposlen u roku od N dana',
    newWorkerNote: 'Bonusi za nove radnike zahtijevaju spisak radnika s datumima zaposlenja, kojeg model za čitanje još nema — ovi iznosi se čuvaju, ali se ne primjenjuju. Norma, obrok i bonus vođe kolone su aktivni.',
    couldNotSave: 'Čuvanje nije uspjelo.',
    saving: 'Čuvanje…',
  },
  bs: {
    title: 'Bonus',
    subtitle: 'Isplata radnicima po koloni — zarada od proizvodnje, učinak po satu, norma i bonusi.',
    previousMonth: 'Prethodni mjesec',
    nextMonth: 'Sljedeći mjesec',
    firma: 'Firma',
    allFirms: 'Sve firme',
    settings: 'Postavke',
    configNote: 'Norma {norm}/h · obrok {meal}/radni dan radnika · bonus vođe {leader}/dan. Stope i pragovi preuzeti su iz radne sveske — prilagodite ih u Postavkama da odgovaraju AU.',
    colCrew: 'Kolona (vođa)',
    colFirma: 'Firma',
    colDays: 'Dani',
    colMetres: 'Metri',
    colHours: 'Sati',
    colEarnings: 'Zarada',
    colPerHour: '€/h',
    colNorm: 'Norma',
    colMeal: 'Obrok',
    colLeader: 'Vođa',
    colTotal: 'Ukupno',
    noProduction: 'Nema proizvodnje ovog mjeseca.',
    totalCrews: { one: 'Ukupno · {count} kolona', few: 'Ukupno · {count} kolone', other: 'Ukupno · {count} kolona' },
    settingsTitle: 'Postavke bonusa',
    settingsSubtitle: 'Pragovi koje koristi obračun.',
    normTarget: 'Ciljna norma (€ po satu)',
    normTargetHint: '"Ispunjena norma" = € po satu ÷ ova vrijednost',
    mealAllowance: 'Topli obrok (€ po radnom danu radnika)',
    crewLeaderBonus: 'Bonus vođe kolone (€ po danu kolone)',
    newWorkerBonus: 'Bonus za nove radnike (€)',
    newWorkerWindow: 'Period „novog radnika” (dani)',
    newWorkerWindowHint: 'zaposlen u roku od N dana',
    newWorkerNote: 'Bonusi za nove radnike zahtijevaju spisak radnika s datumima zaposlenja, kojeg model za čitanje još nema — ovi iznosi se spremaju, ali se ne primjenjuju. Norma, obrok i bonus vođe kolone su aktivni.',
    couldNotSave: 'Spremanje nije uspjelo.',
    saving: 'Spremanje…',
  },
  sr: {
    title: 'Bonus',
    subtitle: 'Isplata radnicima po koloni — zarada od proizvodnje, učinak po satu, norma i bonusi.',
    previousMonth: 'Prethodni mesec',
    nextMonth: 'Sledeći mesec',
    firma: 'Firma',
    allFirms: 'Sve firme',
    settings: 'Podešavanja',
    configNote: 'Norma {norm}/h · obrok {meal}/radni dan radnika · bonus vođe {leader}/dan. Stope i pragovi preuzeti su iz radne sveske — prilagodite ih u Podešavanjima da odgovaraju AU.',
    colCrew: 'Kolona (vođa)',
    colFirma: 'Firma',
    colDays: 'Dani',
    colMetres: 'Metri',
    colHours: 'Sati',
    colEarnings: 'Zarada',
    colPerHour: '€/h',
    colNorm: 'Norma',
    colMeal: 'Obrok',
    colLeader: 'Vođa',
    colTotal: 'Ukupno',
    noProduction: 'Nema proizvodnje ovog meseca.',
    totalCrews: { one: 'Ukupno · {count} kolona', few: 'Ukupno · {count} kolone', other: 'Ukupno · {count} kolona' },
    settingsTitle: 'Podešavanja bonusa',
    settingsSubtitle: 'Pragovi koje koristi obračun.',
    normTarget: 'Ciljna norma (€ po satu)',
    normTargetHint: '"Ispunjena norma" = € po satu ÷ ova vrednost',
    mealAllowance: 'Topli obrok (€ po radnom danu radnika)',
    crewLeaderBonus: 'Bonus vođe kolone (€ po danu kolone)',
    newWorkerBonus: 'Bonus za nove radnike (€)',
    newWorkerWindow: 'Period „novog radnika” (dani)',
    newWorkerWindowHint: 'zaposlen u roku od N dana',
    newWorkerNote: 'Bonusi za nove radnike zahtevaju spisak radnika s datumima zaposlenja, kojeg model za čitanje još nema — ovi iznosi se čuvaju, ali se ne primenjuju. Norma, obrok i bonus vođe kolone su aktivni.',
    couldNotSave: 'Čuvanje nije uspelo.',
    saving: 'Čuvanje…',
  },
})
